import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import CloseIcon from "./icons";
import SPORT, {
  recordString,
  shareText as buildShareText,
} from "../sport/config";
import { posBadgeClass } from "../sport/playerStats";

function makeId() {
  return (
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 6)
  );
}

export default function ShareDialog({
  open,
  onClose,
  roster,
  slots,
  season,
  rating,
  avg,
}) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const sourceRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const createdRef = useRef(false);

  // Render the card → PNG + File whenever the dialog opens
  useEffect(() => {
    if (!open) {
      setImageUrl(null);
      setFile(null);
      setShareUrl(null);
      createdRef.current = false;
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!sourceRef.current || cancelled) return;
      try {
        const dataUrl = await toPng(sourceRef.current, {
          pixelRatio: 2,
          backgroundColor: "#09090b",
          cacheBust: true,
        });
        if (cancelled) return;
        setImageUrl(dataUrl);
        const blob = await (await fetch(dataUrl)).blob();
        if (cancelled) return;
        setFile(
          new File([blob], `${SPORT.siteName}.png`, { type: "image/png" }),
        );
      } catch (e) {
        console.error("Failed to generate preview", e);
      }
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open]);

  // Once the image exists, create a per-result share link (logged-in users)
  useEffect(() => {
    if (!open || !file || !user || createdRef.current) return;
    createdRef.current = true;
    (async () => {
      setCreatingShare(true);
      try {
        const id = makeId();
        const path = `${id}.png`;
        const { error: upErr } = await supabase.storage
          .from("shares")
          .upload(path, file, { contentType: "image/png" });
        if (upErr) throw upErr;
        const {
          data: { publicUrl },
        } = supabase.storage.from("shares").getPublicUrl(path);
        const { error: insErr } = await supabase.from("shares").insert({
          id,
          user_id: user.id,
          wins: season.wins,
          draws: season.draws,
          losses: season.losses,
          rating,
          team_ovr: Math.round(avg || 0),
          image_url: publicUrl,
        });
        if (insErr) throw insErr;
        setShareUrl(`${window.location.origin}/share.php?id=${id}`);
      } catch (e) {
        console.error("Failed to create share link", e);
      } finally {
        setCreatingShare(false);
      }
    })();
  }, [open, file, user, season, rating, avg]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const url =
    shareUrl ||
    (typeof window !== "undefined"
      ? window.location.href
      : `https://${SPORT.domain}`);
  const shareText = buildShareText(season);
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(url);

  const canShareFiles =
    typeof navigator !== "undefined" &&
    !!navigator.canShare &&
    !!file &&
    navigator.canShare({ files: [file] });

  const handleNativeShare = async () => {
    if (!file) return;
    try {
      await navigator.share({
        files: [file],
        title: SPORT.siteName,
        text: `${shareText} ${url}`,
      });
    } catch (e) {
      if (e.name !== "AbortError") console.error("Share failed", e);
    }
  };

  const socials = [
    {
      name: "X",
      icon: XIcon,
      iconColor: "text-zinc-100",
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      bg: "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700",
    },
    {
      name: "Facebook",
      icon: FacebookIcon,
      iconColor: "text-[#1877F2]",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      bg: "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700",
    },
    {
      name: "Reddit",
      icon: RedditIcon,
      iconColor: "text-[#FF4500]",
      href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
      bg: "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700",
    },
  ];

  const allPlayers = slots.map((slot) => roster[slot.id]).filter(Boolean);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Failed to copy", e);
    }
  };

  return (
    <>
      {/* Off-screen 1200x630 capture card */}
      <div
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <div
          ref={sourceRef}
          style={{ width: 1200, height: 630 }}
          className="bg-zinc-950 flex flex-col p-12"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[18px] uppercase tracking-widest text-zinc-500 mb-2">
                Projected record
              </div>
              <div className="flex items-baseline gap-4">
                <span className="text-[84px] font-black text-zinc-100 leading-none tabular-nums">
                  {recordString(season)}
                </span>
                <span className="text-[64px] font-black text-emerald-400 leading-none">
                  {rating}
                </span>
              </div>
            </div>
            {/* <div className="text-right">
              <div className="text-[18px] uppercase tracking-widest text-zinc-500 mb-2">
                Team OVR
              </div>
              <div className="text-[72px] font-black text-zinc-100 leading-none tabular-nums">
                {Math.round(avg || 0)}
              </div>
            </div> */}
          </div>

          <div className="h-px bg-zinc-800 my-8" />

          <div
            className="flex-1 grid grid-cols-2 grid-flow-col gap-x-10 gap-y-3"
            style={{
              gridTemplateRows: `repeat(${Math.ceil(allPlayers.length / 2)}, minmax(0, 1fr))`,
            }}
          >
            {allPlayers.map((p) => (
              <div key={p.id} className="flex items-center gap-4 min-w-0">
                <span
                  className={`shrink-0 w-16 text-center px-2 py-1 rounded-md text-[20px] font-bold ${posBadgeClass(p.pos)}`}
                >
                  {p.pos}
                </span>
                <div className="min-w-0">
                  <div className="text-[26px] font-bold text-zinc-100 truncate leading-tight">
                    {p.name}
                  </div>
                  <div className="text-[16px] text-zinc-500 truncate">
                    {p.club}
                    {p.season ? ` · ${p.season}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-800">
            <span className="text-[28px] font-bold text-zinc-100">
              {SPORT.copy.tagline}
            </span>
            <span className="text-[28px] font-black text-emerald-400">
              {SPORT.domain}
            </span>
          </div>
        </div>
      </div>

      {/* Modal */}
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-6"
        onClick={onClose}
      >
        <div
          className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md
                     p-6 animate-scale-in shadow-2xl shadow-black/50 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-zinc-100">Share your team</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 grid place-items-center rounded-full text-zinc-500
                         hover:text-zinc-100 hover:bg-zinc-900 transition"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="mb-4">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Your team — projected record, rating, and squad"
                className="w-full rounded-xl block border border-zinc-800"
                draggable
              />
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 aspect-[1200/630] grid place-items-center">
                <div className="text-zinc-500 text-sm animate-pulse">
                  Generating preview…
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-zinc-300 mb-4 leading-relaxed">
            {shareText}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 mb-4 text-xs text-zinc-400 font-mono truncate">
            {creatingShare ? "Generating link…" : url}
          </div>

          {canShareFiles && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="w-full py-3 mb-2 rounded-lg bg-emerald-500 text-zinc-950 font-semibold text-sm
                         hover:bg-emerald-400 transition flex items-center justify-center gap-2"
            >
              <ShareIcon className="w-4 h-4" />
              Share image
            </button>
          )}

          {imageUrl && (
            <a
              href={imageUrl}
              download={`${SPORT.siteName}-team.png`}
              className="w-full py-3 mb-4 rounded-lg bg-zinc-900 border border-zinc-800
                         text-sm font-semibold text-zinc-200 hover:border-zinc-700 transition
                         flex items-center justify-center gap-2"
            >
              <DownloadIcon className="w-4 h-4" />
              Save image
            </a>
          )}

          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-2">
            Or share a link
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {socials.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold text-zinc-200 transition ${s.bg}`}
                >
                  <Icon className={`w-4 h-4 ${s.iconColor}`} />
                  <span>{s.name}</span>
                </a>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className="w-full py-3 rounded-lg bg-zinc-900 border border-zinc-800
                       text-sm font-semibold text-zinc-200 hover:border-zinc-700 transition
                       flex items-center justify-center gap-2"
          >
            {copied ? (
              <>
                <span className="text-emerald-400">✓</span>
                Copied!
              </>
            ) : (
              "Copy link"
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
    </svg>
  );
}

function RedditIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.04 1.605a3.32 3.32 0 0 1 .037.495c0 2.55-2.97 4.621-6.628 4.621-3.659 0-6.63-2.071-6.63-4.621 0-.166.014-.331.04-.495-.608-.272-1.043-.889-1.043-1.605 0-.968.786-1.754 1.754-1.754.477 0 .898.182 1.207.49 1.207-.867 2.879-1.43 4.724-1.487l.886-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.111-.726zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
    </svg>
  );
}

function ShareIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function DownloadIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
